import { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { Achievement } from '../../utils/achievements';
import { haptic } from '../../utils/haptics';

interface AchievementCelebrationProps {
  achievement: Achievement;
  onDismiss: () => void;
}

function Particles() {
  const particles = useMemo(() =>
    Array.from({ length: 10 }, (_, i) => {
      const angle = (i / 10) * 360 + Math.random() * 36;
      const dist = 60 + Math.random() * 40;
      const rad = (angle * Math.PI) / 180;
      const x = Math.cos(rad) * dist;
      const y = Math.sin(rad) * dist;
      const size = 3 + Math.random() * 3;
      return { x, y, delay: i * 0.04, size };
    }), []);

  return (
    <>
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-accent animate-particle-burst"
          style={{
            width: p.size, height: p.size,
            top: '50%', left: '50%',
            '--end-x': `${p.x}px`,
            '--end-y': `${p.y}px`,
            animationDelay: `${p.delay}s`,
          } as React.CSSProperties}
        />
      ))}
    </>
  );
}

export function AchievementCelebration({ achievement, onDismiss }: AchievementCelebrationProps) {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => { haptic(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLeaving(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(onDismiss, 400);
    return () => clearTimeout(timer);
  }, [leaving, onDismiss]);

  return createPortal(
    <div
      className={`fixed inset-0 z-[120] flex items-center justify-center ${
        leaving ? 'animate-celebration-out' : 'animate-celebration-in'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-950/85 backdrop-blur-sm"
        onClick={() => setLeaving(true)}
      />

      {/* Card */}
      <div
        className="relative flex flex-col items-center text-center px-6"
        onClick={() => setLeaving(true)}
      >
        {/* Medal */}
        <div className="relative w-[120px] h-[140px] mb-5">
          {/* Glow rings */}
          <div className="absolute bottom-[-10px] left-1/2 -translate-x-1/2 w-[120px] h-[120px] rounded-full border border-accent/15 animate-glow-ring" />
          <div className="absolute bottom-[-30px] left-1/2 -translate-x-1/2 w-[160px] h-[160px] rounded-full border border-accent/15 animate-glow-ring [animation-delay:0.5s]" />
          <div className="absolute bottom-[-50px] left-1/2 -translate-x-1/2 w-[200px] h-[200px] rounded-full border border-accent/15 animate-glow-ring [animation-delay:1s]" />

          {/* Ribbon */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60px] h-[40px]">
            <div className="absolute top-0 left-0.5 w-7 h-10 rounded-b bg-gradient-to-br from-accent to-accent-dark skew-x-[8deg]" />
            <div className="absolute top-0 right-0.5 w-7 h-10 rounded-b bg-gradient-to-br from-accent/90 to-accent-dark -skew-x-[8deg]" />
          </div>

          {/* Medal disc */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[100px] h-[100px] rounded-full flex items-center justify-center animate-medal-shimmer shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-accent)_30%,transparent),0_0_30px_color-mix(in_srgb,var(--color-accent)_30%,transparent),0_8px_32px_rgba(0,0,0,0.4)]"
            style={{ background: 'linear-gradient(145deg, color-mix(in srgb, var(--color-accent) 100%, white 20%), var(--color-accent-dark-val), var(--color-accent-val), color-mix(in srgb, var(--color-accent-dark-val) 100%, black 10%))' }}>
            {/* Inner disc */}
            <div className="w-[78px] h-[78px] rounded-full bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-accent/30 flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-accent/10 via-transparent to-accent/5" />
              <span className="text-[40px] leading-none relative z-10 drop-shadow-[0_0_8px_color-mix(in_srgb,var(--color-accent)_50%,transparent)]" role="img">
                {achievement.icon}
              </span>
            </div>
          </div>

          {/* Particles */}
          <Particles />
        </div>

        <p className="text-[11px] font-bold text-accent uppercase tracking-[3px] mb-1.5 opacity-0 animate-fade-up [animation-delay:0.3s]">
          Badge Unlocked
        </p>
        <h3 className="text-[22px] font-extrabold text-slate-50 mb-1.5 opacity-0 animate-fade-up [animation-delay:0.4s]">
          {achievement.title}
        </h3>
        <p className="text-sm text-slate-400 opacity-0 animate-fade-up [animation-delay:0.5s]">
          {achievement.description}
        </p>

        <p className="text-[10px] text-slate-600 mt-5 opacity-0 animate-fade-up [animation-delay:0.7s]">
          Tap to dismiss
        </p>
      </div>
    </div>,
    document.getElementById('app-shell') || document.body
  );
}
